import {customElement} from "lit/decorators.js";
import {html, type TemplateResult} from "lit";
import {CLOSE_DIALOG} from "../../../page/elements/dialog/dialog";
import {eopCustomEvent} from "../../../common/utils/events";
import {unsafeHTML} from "lit/directives/unsafe-html.js";
import {ClickTracking} from "../../../tracking/clickTracking";
import {resolve} from "../../../container";
import {generateUuid} from "../../../common/crypto";
import {RenderedDialogContent, RenderedDialogs} from "../renderedDialogs";

export class DisclaimerContent extends RenderedDialogContent {

    public constructor(
        dialogId: string,
        public readonly headline: string | undefined,
        public readonly content: string | undefined,
        public readonly url: string,
        public readonly acceptLabel: string,
        public readonly declineLabel: string
    ) {
        super(dialogId);
    }
}

@customElement("eop-news-search-disclaimer-dialogs")
export class EopNewsSearchDisclaimerDialogs extends RenderedDialogs<DisclaimerContent> {

    protected readonly dialogClass = "disclaimer-dialog";

    public constructor(
        private clickTracking: ClickTracking = resolve(ClickTracking)
    ) {
        super();
    }

    protected renderDialogContent(disclaimer: DisclaimerContent): TemplateResult {
        return html`
            <div class="layer-section layer-section-background-white">
                ${this.renderHeadline(disclaimer.headline)}
                ${this.renderContent(disclaimer.content)}
                <div class="disclaimer-footer">
                    <a href=${disclaimer.url}
                       target="_blank"
                       class="uni-button primary button-ok"
                       @click=${(event: Event) => this.acceptCallback(event, disclaimer.acceptLabel)}
                    >${disclaimer.acceptLabel}</a>
                    <button type="button"
                            class="uni-button secondary button-cancel"
                            @click=${(event: Event) => this.declineCallback(event, disclaimer.declineLabel)}
                    >${disclaimer.declineLabel}</button>
                </div>
            </div>
        `;
    }

    private renderHeadline(headline?: string): TemplateResult | null {
        if (!headline) {
            return null;
        }

        return html`
            <h2 class="h">${headline}</h2>
        `;
    }

    private renderContent(content?: string): TemplateResult | null {
        if (!content) {
            return null;
        }

        return html`
            <div>${unsafeHTML(content)}</div>
        `;
    }

    private acceptCallback(event: Event, acceptLabel: string): void {
        this.trackButton(event, acceptLabel);
    }

    private declineCallback(event: Event, declineLabel: string): void {
        this.trackButton(event, declineLabel);
        // event must bubble up to dialog in order to close it; since [this] is the dialogs parent,
        // we must dispatch the event from the currentTarget (the button) to bubble up to the dialog
        (event.currentTarget as HTMLElement).dispatchEvent(eopCustomEvent(CLOSE_DIALOG));
    }

    private trackButton(event: Event, text: string): void {
        this.clickTracking.handleButtonInteract(event, text);
    }

    public create(
        headline: string | undefined,
        content: string | undefined,
        url: string,
        acceptLabel: string,
        declineLabel: string
    ): string {
        const dialogId = "disclaimer-dialog-" + generateUuid();
        this.dialogContents = [...this.dialogContents, new DisclaimerContent(dialogId, headline, content, url, acceptLabel, declineLabel)];
        return dialogId;
    }
}